/**
 * « Securiser » (porteur, 06/10/2026) : signer un document au nom de
 * l'organisation, sans signataire. Trois boutons comme dans « Signer » :
 * sans horodatage certifie / horodatage certifie Adobe / horodatage
 * certifie eIDAS. Le prix est affiche avant, debite a la reussite.
 */
import { useCallback, useEffect, useState } from "react";

import { api, type EtatScellement, type ModeScellement, type Scellement } from "../api";
import { Chargement, Erreur, Vide, date, montant, telecharger } from "../components/ui";

const MODES: { mode: ModeScellement; libelle: string; aide: string }[] = [
  { mode: "sans", libelle: "Sans horodatage certifié",
    aide: "Le document est scellé par Sign365 ; la date est celle de notre serveur." },
  { mode: "adobe", libelle: "Avec horodatage reconnu par Adobe",
    aide: "Date certifiée par un horodateur reconnu par Adobe Acrobat (Sectigo, DigiCert, SSL.com)." },
  { mode: "eidas", libelle: "Avec horodatage qualifié eIDAS",
    aide: "Date certifiée par un horodatage qualifié eIDAS (Certigna)." },
];

export function Securiser() {
  const [mode, setMode] = useState<ModeScellement | null>(null);
  const [devis, setDevis] = useState<{ total_minor: number; currency_code: string } | null>(null);
  const [fichier, setFichier] = useState<File | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<unknown>(null);
  // Demande deposee (file d'attente, 08/10/2026) : son numero et son etat,
  // relu toutes les 2 s tant qu'un travailleur ne l'a pas scellee.
  const [fait, setFait] = useState<{ id: string; etat: EtatScellement } | null>(null);
  const [liste, setListe] = useState<Scellement[] | null>(null);

  const recharger = useCallback(async () => {
    try {
      setListe((await api.scellements()).scellements);
    } catch (e) {
      setErreur(e);
    }
  }, []);
  useEffect(() => {
    void recharger();
  }, [recharger]);

  useEffect(() => {
    if (!fait || fait.etat !== "en_file") return;
    const minuteur = window.setTimeout(() => {
      void api.scellement(fait.id).then(async (s) => {
        setFait({ id: s.id, etat: s.etat });
        if (s.etat !== "en_file") await recharger();
      }, () => setFait({ ...fait }));
    }, 2000);
    return () => window.clearTimeout(minuteur);
  }, [fait, recharger]);

  useEffect(() => {
    if (!mode) return;
    setDevis(null);
    void api.devisScellement(mode).then(setDevis, () => setDevis(null));
  }, [mode]);

  const sceller = async () => {
    if (!mode || !fichier) return;
    setEnvoi(true);
    setErreur(null);
    try {
      const { document_id } = await api.creerDocument(fichier.name.replace(/\.pdf$/i, ""));
      await api.televerserVersion(document_id, fichier);
      const s = await api.sceller(document_id, mode);
      setFait({ id: s.id, etat: s.etat });
      setFichier(null);
      await recharger();
    } catch (e) {
      setErreur(e);
    } finally {
      setEnvoi(false);
    }
  };

  const choisi = MODES.find((m) => m.mode === mode);
  return (
    <>
      <div className="actions actions--split" style={{ margin: "0 0 1rem" }}>
        <div>
          <h1 className="page-title">Sécuriser</h1>
          <p className="page-lead" style={{ margin: 0 }}>
            Signer un document au nom de votre organisation, sans signataire.
          </p>
        </div>
        <div className="actions" style={{ margin: 0 }}>
          {MODES.map((m) => (
            <button key={m.mode} type="button"
                    className={m.mode === mode ? "btn btn--primary" : "btn btn--secondary"}
                    onClick={() => { setMode(m.mode); setFait(null); setErreur(null); }}>
              {m.libelle}
            </button>
          ))}
        </div>
      </div>

      {choisi && (
        <div className="card stack" style={{ marginBottom: "1rem" }}>
          <h2 className="panneau__titre">{choisi.libelle}</h2>
          <p className="muted" style={{ margin: 0 }}>{choisi.aide}</p>
          <label className="dropzone">
            <input type="file" accept="application/pdf,.pdf" style={{ display: "none" }}
                   onChange={(e) => { setFichier(e.target.files?.[0] ?? null); setFait(null); }} />
            {fichier ? <strong>{fichier.name}</strong> : <strong>Choisissez le PDF à sécuriser</strong>}
          </label>
          <p style={{ margin: 0 }}>
            {devis ? <>Coût : <strong>{montant(devis.total_minor, devis.currency_code)}</strong>, débité à l'envoi de la demande.</>
              : "Tarif en cours de chargement ou non configuré."}
          </p>
          <Erreur erreur={erreur} />
          {fait && (
            <div className="alert alert--info">
              Demande n° <strong>{fait.id}</strong> enregistrée.{" "}
              {fait.etat === "en_file" && "Scellement en cours…"}
              {fait.etat === "echec" && "Le scellement a échoué."}
              {fait.etat === "scelle" && (
                <>
                  Document sécurisé.{" "}
                  <button type="button" className="btn btn--link btn--inline"
                          onClick={() => void api.documentScelle(fait.id).then((b) => telecharger(b, "document - scellé.pdf"))}>
                    Télécharger
                  </button>
                </>
              )}
            </div>
          )}
          <div className="actions">
            <button type="button" className="btn btn--primary" disabled={!fichier || envoi}
                    onClick={() => void sceller()}>
              {envoi ? "Scellement en cours…" : "Sécuriser le document"}
            </button>
          </div>
        </div>
      )}

      <div className="card">
        {liste === null ? <Chargement /> : liste.length === 0 ? <Vide>Aucun document sécurisé.</Vide> : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Document</th><th>Option</th><th>Horodateur</th><th>Coût</th><th>Le</th><th /></tr>
              </thead>
              <tbody>
                {liste.map((s) => (
                  <tr key={s.id}>
                    <td>{s.title}</td>
                    <td>{MODES.find((m) => m.mode === s.mode)?.libelle}</td>
                    <td>{s.horodateur ?? "—"}</td>
                    <td>{montant(s.amount_minor, s.currency_code)}</td>
                    <td>{date(s.created_at, true)}</td>
                    <td className="num">
                      {s.etat === "scelle" ? (
                        <button type="button" className="btn btn--link"
                                onClick={() => void api.documentScelle(s.id).then((b) => telecharger(b, s.filename ?? "document - scellé.pdf"))}>
                          Télécharger
                        </button>
                      ) : s.etat === "en_file" ? <span className="muted">En cours…</span> : <span className="muted">Échec</span>}
                      {/* Un dossier de preuve par enveloppe payee (Franck, 08/10/2026). */}
                      {s.dossier && (
                        <button type="button" className="btn btn--link"
                                onClick={() => void api.dossierScellement(s.id).then((b) => telecharger(b, `dossier-de-preuve-${s.id}.pdf`))}>
                          Dossier de preuve
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
